import { FaroTilesActionBar } from "@components/common/faro-card/faro-tiles-action-bar";
import { CardSkeleton } from "@components/common/skeleton-loading/card-skeleton";
import { GroupCard } from "@components/group-card/group-card";
import { GroupsSort } from "@components/groups/groups-sort";
import { BulkExportGroups } from "@components/table/groups/bulk-actions/bulk-export-groups";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import { GroupsSortBy } from "@custom-types/group-types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { fetchingGroupsFlagsSelector } from "@store/groups/groups-selector";
import { useAppSelector } from "@store/store-helper";
import { UI_HEIGHTS_VIRTUALIZATION_GRID } from "@styles/common-styles";

interface Props {
  /** List of all the available groups in the company */
  groups: FaroCoreApiTypes.GroupSummaryResponse[];

  /** Current sort by value */
  sortBy: GroupsSortBy;

  /** Callback to update the sort by value */
  setSortBy: (sortBy: GroupsSortBy) => void;
}

/**
 * Lists all the available groups in the company as cards.
 */
export function GroupsTiles({ groups, sortBy, setSortBy }: Props): JSX.Element {
  const { isFetchingGroups } = useAppSelector(fetchingGroupsFlagsSelector);

  const { isScreenSmOrSmaller } = useMediaQueryList();
  
  /** Determine whether to show the export button */
  const shouldShowExportButton = isFetchingGroups || groups.length > 0;

  const tabBarHeight = isScreenSmOrSmaller
    ? UI_HEIGHTS_VIRTUALIZATION_GRID.tabGroupBar.smallScreen
    : UI_HEIGHTS_VIRTUALIZATION_GRID.tabGroupBar.default;

  /**
   * Total vertical UI height above the VirtualizedGrid.
   * The grid fills the rest of the viewport height.
   */
  const VIRTUAL_GRID_OFFSET =
    UI_HEIGHTS_VIRTUALIZATION_GRID.appBar +
    UI_HEIGHTS_VIRTUALIZATION_GRID.bulkActionsBar +
    UI_HEIGHTS_VIRTUALIZATION_GRID.contentPadding +
    UI_HEIGHTS_VIRTUALIZATION_GRID.paddingTiles +
    tabBarHeight;

  /** Right actions for the tiles action bar */
  const rightActions = shouldShowExportButton
    ? [
        <BulkExportGroups
          key="BulkExportGroups"
          selectedGroups={groups}
          /* It is false since entities can't be selected in tiles view */
          areEntitiesSelected={false}
        />,
      ]
    : [];

  return (
    <Box component="div">
      <FaroTilesActionBar
        leftActions={[
          <GroupsSort key="groups-sort" sortBy={sortBy} setSortBy={setSortBy} />,
        ]}
        rightActions={rightActions}
        isLoading={isFetchingGroups}
      />

      <VirtualizedGrid
        // The `key` here forces React to remount the grid whenever `groups` changes.
        // This ensures that the scroll position resets and the grid re-renders from the first item
        key={groups.length}
        items={groups}
        isLoading={isFetchingGroups}
        renderSkeletonItem={(i) => (
          <CardSkeleton key={`skeleton-group-${i}`} type="group" />
        )}
        height={`calc(100vh - ${VIRTUAL_GRID_OFFSET}px)`}
        renderItem={(group) => (
          <GroupCard key={group.id} group={group} />
        )}
      />
    </Box>
  );
}
